import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import { useBrokerFeature } from "@rentiohq/shared-frontend/dist/redux/broker/broker.hooks";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { generateFormId } from "@rentiohq/shared-frontend/dist/redux/form/form.utils";
import * as applicationActions from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.actions";
import * as applicationHooks from "@rentiohq/shared-frontend/dist/reduxV2/applications/application.hooks";
import * as propertyHooks from "@rentiohq/shared-frontend/dist/reduxV2/property/property.hooks";
import * as propertyAmenityHooks from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.hooks";
import { getAmenityNumberValue } from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity/propertyAmenity.utils";
import {
  EIntakeStep,
  IApplication,
} from "@rentiohq/shared-frontend/dist/types/application.types";
import { EBrokerFeature } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { isBrusselsZip } from "@rentiohq/shared-frontend/dist/utils/property.utils";
import { calculateRentMatchScore } from "@rentiohq/shared-frontend/dist/utils/rentmatch.utils";
import {
  Checkbox,
  DropdownMenu,
  Icon,
  MultiStepForm,
} from "@rentiohq/web-shared/dist/components";
import utils from "@rentiohq/web-shared/dist/utils";
import React, { useContext } from "react";
import {
  Draggable,
  DraggableProvided,
  DraggableStateSnapshot,
} from "react-beautiful-dnd";
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { StringParam } from "serialize-query-params";
import { getPhaseForStep } from "utils/application.utils";
import { Rating } from "../../../../../../components/Rating";
import createDuplicateApplicationForm from "../../../../../../forms/duplicateApplication";
import * as t from "../../../../../../services/translationService";
import { DragAndDropContext } from "../../context/DragAndDropContext";
import { IApplicationDropDownHandler } from "../../PropertyDetailApplications";
import { RM_COLORS } from "../../rentmatch.colors";
import {
  EPdfStatus,
  getContactActions,
  getViewActions,
} from "../applicationActions.utils";
import { getResidentsCount, getTotalIncome } from "../DragItem/dragItem.utils";
import { OtherApplicationsModal } from "../OtherApplicationsModal";
import { WarningTooltip } from "../WarningTooltip";
import * as S from "./ApplicationTable.styled";

interface IProps {
  item: IApplication;
  step: number;
  index: number;
  allowedSteps: number[];
  isDisabled?: boolean;
  isSelected: boolean;
  handler: IApplicationDropDownHandler;
  renderAsDragItem?: boolean;
  applicationVisit?: any;
}

const formId = generateFormId();

// Tabs from "Candidate tenant" onward (+ archive, rendered with step 0) where a
// submitted extended rental CV is the relevant milestone.
const SUBMITTED_STATUS_STEPS: number[] = [
  0,
  EIntakeStep.candidateTenant,
  EIntakeStep.tenant,
  EIntakeStep.notEligible,
];

// Small ID / income document-status badge shown under the RentMatch score.
// A corner circle marks the state: green + white check = all delivered,
// red + white dash = none delivered. When only part of the required documents
// are in (e.g. one of two tenants), an amber "delivered/total" fraction is
// shown instead (e.g. "1/2").
const DocStatusBadge = (props: {
  type: "id" | "income";
  delivered: number;
  total: number;
  title: string;
}) => {
  const { type, delivered, total, title } = props;
  let state: "full" | "none" | "partial" = "partial";
  if (total === 0 || delivered >= total) {
    state = "full";
  } else if (delivered === 0) {
    state = "none";
  }

  return (
    <span
      title={`${title}${total > 0 ? ` — ${delivered}/${total}` : ""}`}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        color: RM_COLORS.gray700,
      }}
    >
      {type === "id" ? (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <circle cx="8" cy="12" r="2.2" />
          <path d="M13 10.5h5M13 14h5" />
        </svg>
      ) : (
        <span style={{ fontSize: "14px", fontWeight: 700, lineHeight: 1 }}>
          €
        </span>
      )}

      {state === "partial" ? (
        <span
          style={{
            marginLeft: "2px",
            fontSize: "10px",
            fontWeight: 700,
            color: RM_COLORS.amberDark,
            lineHeight: 1,
          }}
        >
          {delivered}/{total}
        </span>
      ) : (
        <span
          style={{
            position: "absolute",
            top: "-4px",
            right: "-9px",
            width: "9px",
            height: "9px",
            borderRadius: "50%",
            background:
              state === "full" ? RM_COLORS.greenStrong : RM_COLORS.red,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {state === "full" ? (
            <svg
              width="6"
              height="6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg
              width="6"
              height="6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="white"
              strokeWidth="5"
              strokeLinecap="round"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          )}
        </span>
      )}
    </span>
  );
};

export const ApplicationTableRow = React.memo((props: IProps) => {
  const {
    item,
    step,
    handler,
    index,
    isDisabled: _isDisabled,
    isSelected: _isSelected,
    renderAsDragItem = true,
    applicationVisit,
  } = props;

  const [, setQueryParamValue] = useQueryParams({
    applicationId: StringParam,
  });

  const [clickX, setClickX] = React.useState(0);

  const dispatch = useDispatch();
  const { broker } = authHooks.useSelf();

  const intakeType = broker?.intakeType || 2;
  const hasCalendarSync = useBrokerFeature(EBrokerFeature.CalendarSync);

  const {
    onSelectItem,
    onUnselectItem,
    replaceSelectedItems,
    selectedItems,
    isGlobalDragging,
  } = useContext(DragAndDropContext);

  const params = useParams<{ propertyId: string }>();
  const propertyId = params.propertyId ? +params.propertyId : undefined;

  applicationHooks.useArchive({});
  applicationHooks.useUnarchive({});

  const { detail: property } = propertyHooks.useDetail({
    shouldRefetch: false,
    id: propertyId,
  });

  const { items: propertyAmenities } = propertyAmenityHooks.useGetByPropertyId({
    propertyId,
  });

  const selectedItemIds = selectedItems.map(si => si.id) || [];

  const [showDuplicateModal, setShowDuplicateModal] =
    React.useState<boolean>(false);
  const [showOtherAppsModal, setShowOtherAppsModal] =
    React.useState<boolean>(false);
  const [pdfStatus, setPdfStatus] = React.useState(EPdfStatus.Checking);
  const { contacts, residents } = item;

  const totalIncome = getTotalIncome(contacts);
  const adultCount = getResidentsCount(residents, "INTAKE_RESIDENT_ADULTS");
  const childrenCount = getResidentsCount(
    residents,
    "INTAKE_RESIDENT_CHILDREN",
  );
  const petCount = residents.reduce(
    (currentPetCount, resident) =>
      resident.id.includes("_PETS_")
        ? currentPetCount + resident.value
        : currentPetCount,
    0,
  );

  let totalDocuments = 0;
  let deliveredDocuments = 0;

  contacts.forEach((contact: any) => {
    const propertyInBrussels = property?.zip && isBrusselsZip(property.zip);

    if (!propertyInBrussels) {
      totalDocuments++;
      if (contact.documents.length > 0) deliveredDocuments++;
    }

    contact.income.forEach((income: any) => {
      totalDocuments++;
      if (income.documents && income.documents.length > 0) deliveredDocuments++;
    });
  });

  // Document-status counts for the ID / income badges. Every tenant needs their
  // own ID + income document(s), so we count delivered vs. required. ID is not
  // collected for Brussels properties, so nothing is required there.
  const propertyInBrussels = !!(property?.zip && isBrusselsZip(property.zip));
  const idTotal = propertyInBrussels ? 0 : contacts.length;
  const idDelivered = contacts.filter(
    (contact: any) => (contact.documents?.length ?? 0) > 0,
  ).length;
  const allIncomes = contacts.flatMap((contact: any) => contact.income || []);
  const incomeTotal = allIncomes.length;
  const incomeDelivered = allIncomes.filter(
    (income: any) => (income.documents?.length ?? 0) > 0,
  ).length;

  // A candidacy counts as "submitted" once the current phase is completed
  // (stepCompletedAt is set → the "application submitted" status shown in the
  // MHP view), only relevant from the candidate-tenant tab onward + archive.
  const hasSubmittedApplication =
    SUBMITTED_STATUS_STEPS.includes(step) && !!item.stepCompletedAt;

  const handleDuplicate = ({ step, propertyId: newPropertyId }: any) => {
    if (!newPropertyId) {
      return;
    }

    dispatch(
      applicationActions.duplicateApplicationStart.getAction({
        id: item.id,
        propertyId: newPropertyId,
        step,
        onSuccess(result) {
          handler?.([result.id], step, result);
        },
      }),
    );

    setShowDuplicateModal(false);
  };

  const handleCloseModal = () => {
    setShowDuplicateModal(false);
  };

  const { archive } = applicationHooks.useArchive({});
  const { unarchive } = applicationHooks.useUnarchive({});

  const checkAvailability = async () => {
    if (!item?.pdfUrl) {
      setPdfStatus(EPdfStatus.NotAvailable);
      return;
    }
    try {
      // Use a ranged GET (not HEAD): the signed S3 URLs reject/ignore HEAD,
      // which left the status stuck on "generating" so the CV could never be
      // opened. The Range header fetches only the first byte instead of the
      // whole PDF just to check existence (S3 answers 206 on success).
      const response = await fetch(item.pdfUrl as URL | RequestInfo, {
        method: "GET",
        headers: { Range: "bytes=0-1" },
      });
      const isAvailable = response.status === 200 || response.status === 206;
      setPdfStatus(isAvailable ? EPdfStatus.Generated : EPdfStatus.Generating);
    } catch {
      setPdfStatus(EPdfStatus.NotAvailable);
    }
  };

  const getRowActions = () => {
    const duplicateActions = [
      {
        content: t.applicationDuplicateApplicationAction(),
        onClick: () => setShowDuplicateModal(true),
      },
    ];

    const contactRowActions = getContactActions({ item });

    const viewActions = getViewActions({
      item,
      pdfStatus,
      setQueryParamValue,
      isDetailView: false,
    });

    const archiveActions = [
      {
        content: item.archivedAt
          ? getLocalizedText("applications.cta.unarchive")
          : getLocalizedText("applications.cta.archive"),
        onClick: () => {
          if (item.archivedAt) {
            unarchive({}, item.id);
          } else {
            archive({}, item.id);
          }
        },
      },
    ];

    return [duplicateActions, contactRowActions, viewActions, archiveActions];
  };

  if (!propertyId) {
    return null;
  }

  const handleSelectNewItem = (item: IApplication) => {
    const isFirstSelection = selectedItems.length === 0;
    if (isFirstSelection) {
      onSelectItem(item);
      return;
    }

    // In the archive the rows are rendered with step === 0, which has no phase
    // (getPhaseForStep returns -1). Archived candidates are not phase-grouped,
    // so allow any combination to be multi-selected together instead of
    // replacing the previous selection on every click.
    if (step === 0) {
      onSelectItem(item);
      return;
    }

    const phaseForOtherItems = getPhaseForStep(
      intakeType,
      selectedItems[0].step,
    );
    const phaseForNewItem = getPhaseForStep(intakeType, step);

    if (phaseForOtherItems === phaseForNewItem) {
      onSelectItem(item);
    } else {
      replaceSelectedItems([item]);
    }
  };

  const handleSelectItem = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (!selectedItemIds.includes(item.id)) {
      handleSelectNewItem(item);
    } else {
      onUnselectItem(item);
    }
  };

  const renderRowContent = () => {
    // Determine the primary contact and application counts
    const primaryContact = contacts[0] || {};
    const propertyCount = (item as any).otherApplicationsTotalCount || 0;

    // Calculate rent ratio
    const rentPrice = (property as any)?.rentPrice || 0;
    const rentRatio = rentPrice > 0 ? totalIncome / rentPrice : 0;
    const formattedRatio =
      rentRatio > 0
        ? `(${rentRatio.toLocaleString("nl-BE", { maximumFractionDigits: 1 })})`
        : "";

    const hasNoShows = !!item.otherApplicationsMeta?.noShows?.length;
    const hasNotes = !!item.otherApplicationsMeta?.notes?.length;
    const hasCurrentNoShow = !!item.noShowAt;
    const internalNoteContent =
      (item.otherApplicationsMeta as any)?.internalNote ||
      (primaryContact as any).note;
    const hasInternalNote = !!internalNoteContent;
    const hasCrossPropertyWarnings =
      hasNoShows || hasNotes || hasCurrentNoShow || hasInternalNote;

    // Step badge label (current step) — skip for archived (step=0)
    let stepLabel =
      step > 0
        ? t.applicationStateTitleRentmatch(step as unknown as EIntakeStep)
        : "";

    // Distinguish a candidacy that landed on the waiting list automatically from
    // one a broker dragged there manually.
    if (step === EIntakeStep.waitingList && item.autoWaitlisted) {
      stepLabel = getLocalizedText(
        "property.dashboard.application.7.title.automatic",
      );
    }

    if (
      step === EIntakeStep.visitor &&
      (applicationVisit?.start || applicationVisit?.slotStart)
    ) {
      stepLabel = t.applicationStateVisitScheduled();
    }

    // RentMatch Calculation
    const matchBasePrice = property?.price || 0;
    const matchCosts = (property as any)?.costs || 0;

    const propertyDetails = {
      rent: matchBasePrice + matchCosts,
      bedrooms: propertyAmenities
        ? getAmenityNumberValue(
            propertyAmenities,
            "rentio.default.room_count_bedroom",
          ) || null
        : null,
      availableDate: null, // Ignore date penalty for now
      hasBalcony: false,
      hasGarden: false,
      floorType: "other" as const,
    };

    const userDetails = {
      netIncome: totalIncome,
      otherIncome: 0,
      adults: adultCount,
      minors: childrenCount,
      moveInDateType: item.dateOfOccupancy
        ? ("specific" as const)
        : ("flexible" as const),
      specificMoveInDate: item.dateOfOccupancy
        ? String(item.dateOfOccupancy)
        : undefined,
    };

    const rentMatch =
      propertyDetails.rent > 0
        ? calculateRentMatchScore(userDetails, propertyDetails)
        : null;

    const getRentBgColor = (score: number) => {
      if (score >= 80) return RM_COLORS.greenBgSoft;
      if (score >= 50) return RM_COLORS.amberBg;
      return RM_COLORS.redBg;
    };

    const getRentTextColor = (score: number) => {
      if (score >= 80) return RM_COLORS.greenDarker;
      if (score >= 50) return RM_COLORS.amberDark;
      return RM_COLORS.redDark;
    };

    return (
      <>
        <S.Cell
          onClick={(e: React.MouseEvent) => {
            // Keep a checkbox click from bubbling to the row's onClick (which
            // opens the detail drawer). In the draggable tabs react-beautiful-dnd
            // already swallows this click, but the archive rows are not
            // draggable, so without this the drawer opened and the selection was
            // lost — breaking independent multi-select in the archive.
            e.stopPropagation();
          }}
        >
          <Checkbox
            onChangeValue={handleSelectItem}
            checked={selectedItemIds.includes(item.id)}
            appearance={
              selectedItemIds.includes(item.id) ? "success" : "default"
            }
            error={false}
          />
        </S.Cell>

        {/* Candidate / Contact */}
        <S.CandidateNameContainer>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <S.CandidateName>{getName(primaryContact)}</S.CandidateName>
            {/* Warning triangle — visible when cross-property warnings OR internal notes exist */}
            {hasCrossPropertyWarnings && (
              <WarningTooltip
                size={18}
                tooltipContent={
                  <div style={{ maxWidth: "240px" }}>
                    {hasCurrentNoShow && (
                      <div>
                        <strong>
                          {getLocalizedText(
                            "application.cross_property.tooltip.noshow",
                          )}
                        </strong>
                        <br />
                        {formatDate(new Date(item.noShowAt!), "d MMMM yyyy")}
                        <br />
                        {property ? formatAddress(property as any) : ""}
                      </div>
                    )}
                    {hasNoShows &&
                      item.otherApplicationsMeta!.noShows.map((ns, idx) => (
                        <React.Fragment key={`ns-${idx}`}>
                          {(hasCurrentNoShow || idx > 0) && (
                            <hr
                              style={{
                                border: "none",
                                borderTop: `1px solid ${RM_COLORS.amberBorder}`,
                                margin: "5px 0",
                              }}
                            />
                          )}
                          <div>
                            <strong>
                              {getLocalizedText(
                                "application.cross_property.tooltip.noshow",
                              )}
                            </strong>
                            <br />
                            {formatDate(new Date(ns.noShowAt), "d MMMM yyyy")}
                            <br />
                            {ns.propertyAddress}
                          </div>
                        </React.Fragment>
                      ))}
                    {hasInternalNote && (
                      <>
                        {(hasCurrentNoShow || hasNoShows) && (
                          <hr
                            style={{
                              border: "none",
                              borderTop: `1px solid ${RM_COLORS.amberBorder}`,
                              margin: "5px 0",
                            }}
                          />
                        )}
                        <div
                          style={{ fontStyle: "italic", marginBottom: "6px" }}
                        >
                          "{internalNoteContent}"
                        </div>
                      </>
                    )}
                    {hasNotes &&
                      item.otherApplicationsMeta!.notes.map((n, idx) => (
                        <React.Fragment key={`note-${idx}`}>
                          {(hasCurrentNoShow ||
                            hasNoShows ||
                            hasInternalNote ||
                            idx > 0) && (
                            <hr
                              style={{
                                border: "none",
                                borderTop: `1px solid ${RM_COLORS.amberBorder}`,
                                margin: "5px 0",
                              }}
                            />
                          )}
                          <div style={{ fontStyle: "italic" }}>
                            "{n.content}"<br />
                            {n.propertyAddress}
                          </div>
                        </React.Fragment>
                      ))}
                  </div>
                }
              />
            )}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              marginTop: "2px",
            }}
          >
            <Icon source="keys" size="extraSmall" />
            {propertyCount > 1 ? (
              <div
                onClick={e => e.stopPropagation()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  fontSize: "11px",
                }}
              >
                <span
                  role="button"
                  tabIndex={0}
                  style={{
                    fontSize: "11px",
                    color: RM_COLORS.blueStrong,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                  onClick={() => setShowOtherAppsModal(true)}
                  onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                      setShowOtherAppsModal(true);
                    }
                  }}
                >
                  {getLocalizedText(
                    "application.cross_property.tooltip.multiple_applications",
                    { count: propertyCount },
                  )}
                </span>
              </div>
            ) : (
              <span style={{ fontSize: "11px", color: RM_COLORS.gray400 }}>
                {getLocalizedText(
                  "application.cross_property.tooltip.no_other_applications",
                )}
              </span>
            )}
          </div>
        </S.CandidateNameContainer>

        {/* RentMatch Score */}
        <S.Cell>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "5px",
              minWidth: 0,
            }}
          >
            {rentMatch ? (
              <S.RentMatchBadge
                bgColor={getRentBgColor(rentMatch.score)}
                textColor={getRentTextColor(rentMatch.score)}
                title={rentMatch.warnings.map(w => w.message).join("\n")}
              >
                {rentMatch.score}%
              </S.RentMatchBadge>
            ) : (
              <S.RentMatchBadge>-</S.RentMatchBadge>
            )}
            {/* Document status (ID + income) — only once the CV is submitted. */}
            {hasSubmittedApplication && (
              <div
                style={{ display: "flex", alignItems: "center", gap: "12px" }}
              >
                <DocStatusBadge
                  type="id"
                  delivered={idDelivered}
                  total={idTotal}
                  title={getLocalizedText("intake.contract.id.title")}
                />
                <DocStatusBadge
                  type="income"
                  delivered={incomeDelivered}
                  total={incomeTotal}
                  title={getLocalizedText("intake.financial.title")}
                />
              </div>
            )}
          </div>
        </S.Cell>

        {/* Activity */}
        <S.Cell>
          <S.CandidateNameContainer>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              {/* Green check only once the candidate has actually submitted
                  their extended rental CV (phase completed → stepCompletedAt),
                  and only in the candidate-tenant/tenant/not-eligible/archive
                  tabs. */}
              {hasSubmittedApplication && (
                <span
                  title={`${t.applicationStepCompletedAtRentmatch(
                    step,
                  )} ${utils.date.format(
                    item.stepCompletedAt,
                    "dd/MM/yyyy HH:mm",
                  )}`}
                  style={{
                    display: "inline-flex",
                    color: RM_COLORS.greenStrong,
                  }}
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
              )}
              <span style={{ fontSize: "13px", color: RM_COLORS.gray700 }}>
                {step === EIntakeStep.visitor &&
                (applicationVisit?.start || applicationVisit?.slotStart)
                  ? utils.date.format(
                      new Date(
                        applicationVisit.start || applicationVisit.slotStart,
                      ),
                      "dd/MM/yyyy HH:mm",
                    )
                  : utils.date.format(
                      item.stepCompletedAt || item.stepStartedAt,
                      "dd/MM/yyyy HH:mm",
                    )}
              </span>
            </div>
            {stepLabel && (
              <span
                style={{
                  display: "inline-block",
                  marginTop: "3px",
                  padding: "2px 8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: RM_COLORS.greenStrong,
                  backgroundColor: RM_COLORS.greenBgAlt,
                  borderRadius: "9999px",
                  width: "fit-content",
                }}
              >
                {stepLabel}
              </span>
            )}
          </S.CandidateNameContainer>
        </S.Cell>

        {/* Income */}
        <S.Cell>
          <span
            style={{
              fontSize: "13px",
              fontWeight: 600,
              color: totalIncome === 0 ? RM_COLORS.gray400 : RM_COLORS.gray900,
            }}
          >
            {formatCurrency(totalIncome)}
          </span>
          {formattedRatio && (
            <span
              style={{
                fontSize: "13px",
                color: RM_COLORS.gray500,
                marginLeft: "4px",
              }}
            >
              {formattedRatio}
            </span>
          )}
        </S.Cell>

        {/* More info */}
        <S.Cell>
          <S.InfoGroup>
            <S.InfoItem>
              <Icon source="contact" size="small" />
              <span style={{ fontSize: "13px", color: RM_COLORS.gray500 }}>
                {adultCount}
              </span>
            </S.InfoItem>
            <S.InfoItem>
              <Icon source="propellerHat" size="small" />
              <span style={{ fontSize: "13px", color: RM_COLORS.gray500 }}>
                {childrenCount}
              </span>
            </S.InfoItem>
            {petCount > 0 && (
              <S.InfoItem>
                <Icon source="pets" size="small" />
                <span style={{ fontSize: "13px", color: RM_COLORS.gray500 }}>
                  {petCount}
                </span>
              </S.InfoItem>
            )}
          </S.InfoGroup>
        </S.Cell>

        {/* Rating */}
        <S.Cell>
          <div onClick={e => e.stopPropagation()}>
            <Rating
              value={item.rating || 0}
              size={14}
              onChange={value => {
                dispatch(
                  applicationActions.setApplicationRatingStart.getAction({
                    id: item.id,
                    value,
                  }),
                );
              }}
            />
          </div>
        </S.Cell>

        {/* Actions */}
        <S.ActionsCell>
          {(() => {
            // Define action buttons per step
            type StepAction = {
              label: string;
              targetStep?: EIntakeStep;
              icon: "positive" | "negative" | "hourglass";
              customAction?: () => void;
            };
            const actions: StepAction[] = [];

            switch (step) {
              case EIntakeStep.candidateVisitor:
                actions.push(
                  hasCalendarSync
                    ? {
                        label: getLocalizedText("application.action.invite"),
                        targetStep: EIntakeStep.invited,
                        icon: "positive",
                      }
                    : {
                        label: getLocalizedText("application.action.visit"),
                        targetStep: EIntakeStep.visitor,
                        icon: "positive",
                      },
                );
                actions.push({
                  label: getLocalizedText("application.action.waitlist"),
                  targetStep: EIntakeStep.waitingList,
                  icon: "hourglass",
                });
                break;

              case EIntakeStep.waitingList:
                actions.push(
                  hasCalendarSync
                    ? {
                        label: getLocalizedText("application.action.invite"),
                        targetStep: EIntakeStep.invited,
                        icon: "positive",
                      }
                    : {
                        label: getLocalizedText("application.action.visit"),
                        targetStep: EIntakeStep.visitor,
                        icon: "positive",
                      },
                );
                actions.push({
                  label: getLocalizedText("application.action.reject"),
                  targetStep: EIntakeStep.notEligible,
                  icon: "negative",
                });
                break;

              case EIntakeStep.planAsVisitor:
                actions.push(
                  hasCalendarSync
                    ? {
                        label: getLocalizedText("application.action.invite"),
                        targetStep: EIntakeStep.invited,
                        icon: "positive",
                      }
                    : {
                        label: getLocalizedText("application.action.visit"),
                        targetStep: EIntakeStep.visitor,
                        icon: "positive",
                      },
                );
                actions.push({
                  label: getLocalizedText("application.action.reject"),
                  targetStep: EIntakeStep.notEligible,
                  icon: "negative",
                });
                break;

              case EIntakeStep.invited:
                actions.push({
                  label: getLocalizedText("application.action.visit_no_check"),
                  targetStep: EIntakeStep.visitor,
                  icon: "positive",
                });
                actions.push({
                  label: getLocalizedText("application.action.reject"),
                  targetStep: EIntakeStep.notEligible,
                  icon: "negative",
                });
                break;

              case EIntakeStep.visitor:
                actions.push({
                  label: getLocalizedText("application.action.candidate"),
                  targetStep: EIntakeStep.candidateTenant,
                  icon: "positive",
                });
                actions.push({
                  label: item.noShowAt
                    ? getLocalizedText("application.action.remove_no_show")
                    : getLocalizedText("application.action.no_show"),
                  icon: "negative",
                  customAction: () =>
                    dispatch(
                      applicationActions.setApplicationNoShowStart.getAction({
                        id: item.id,
                        value: !item.noShowAt,
                      }),
                    ),
                });
                break;

              case EIntakeStep.candidateTenant:
                actions.push({
                  label: getLocalizedText("application.action.tenant"),
                  targetStep: EIntakeStep.tenant,
                  icon: "positive",
                });
                actions.push({
                  label: getLocalizedText("application.action.reject"),
                  targetStep: EIntakeStep.notEligible,
                  icon: "negative",
                });
                break;

              case EIntakeStep.tenant:
                break;

              case EIntakeStep.notEligible:
                // Archive is handled differently — typically archiving the application
                break;
            }

            return actions.map((action, idx) => (
              <S.ActionButton
                key={idx}
                onClick={e => {
                  e.stopPropagation();
                  if (action.customAction) {
                    action.customAction();
                  } else if (action.targetStep) {
                    handler([item.id], action.targetStep);
                  }
                }}
              >
                {action.icon === "positive" && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
                    <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3" />
                  </svg>
                )}
                {action.icon === "negative" && (
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z" />
                    <path d="M17 2h4a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2h-4" />
                  </svg>
                )}
                {action.icon === "hourglass" && (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 22h14" />
                    <path d="M5 2h14" />
                    <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                    <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                  </svg>
                )}
                {action.label}
              </S.ActionButton>
            ));
          })()}

          {/* Chevron / Dropdown menu */}
          <S.ChevronMenuWrapper onClick={(e: any) => e.stopPropagation()}>
            <DropdownMenu
              onOpen={checkAvailability}
              children={
                <S.ChevronButton>
                  <svg
                    width="8"
                    height="12"
                    viewBox="0 0 8 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="1 1 7 6 1 11" />
                  </svg>
                </S.ChevronButton>
              }
              actions={getRowActions()}
            />
          </S.ChevronMenuWrapper>
        </S.ActionsCell>
      </>
    );
  };

  if (!renderAsDragItem) {
    return (
      <>
        <S.TableRow
          onClick={() => {
            setQueryParamValue({ applicationId: item.id });
          }}
        >
          {renderRowContent()}
        </S.TableRow>
        {showDuplicateModal && (
          <MultiStepForm
            formId={`duplicate-application-${formId}`}
            schemas={createDuplicateApplicationForm({ broker, propertyId })}
            asModal={true}
            withAside={false}
            onSuccess={handleDuplicate}
            modalProps={{ onClose: handleCloseModal, width: "small" }}
          />
        )}
        {showOtherAppsModal && (
          <OtherApplicationsModal
            candidateName={getName(contacts[0] || {})}
            properties={item.otherApplicationsMeta?.properties ?? []}
            onClose={() => setShowOtherAppsModal(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Draggable draggableId={item.id} index={index} key={item.id}>
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
          if (
            isGlobalDragging &&
            selectedItemIds.includes(item.id) &&
            !snapshot.isDragging
          ) {
            return (
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{ ...provided.draggableProps.style, display: "none" }}
              />
            );
          }

          if (snapshot.isDragging) {
            // Destructure width/height to prevent the measured row width from
            // causing horizontal overflow in the scrollable Table container.
            const {
              width: _w,
              height: _h,
              ...dragStyle
            } = (provided.draggableProps.style ?? {}) as any;
            // Render the drag clone in a portal on document.body so its
            // position:fixed styling is no longer scoped to the scrollable
            // Table container — otherwise the clone (offset by the grabbed
            // row's coordinates) expands the table's horizontal scroll area.
            return createPortal(
              <div
                ref={provided.innerRef}
                {...provided.draggableProps}
                {...provided.dragHandleProps}
                style={{
                  ...dragStyle,
                  display: "flex",
                  alignItems: "center",
                  background: "transparent",
                  border: "none",
                  boxShadow: "none",
                  padding: 0,
                  width: "auto",
                  height: "auto",
                  zIndex: 9999,
                }}
              >
                <div style={{ marginLeft: Math.max(0, clickX - 90) + "px" }}>
                  <S.DragPreviewPill>
                    <div
                      style={{
                        marginRight: "6px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                        <circle cx="12" cy="7" r="4" />
                      </svg>
                    </div>
                    {selectedItemIds.length > 1
                      ? getLocalizedText(
                          "rentmatch.applications.drag_count_selected",
                          { count: String(selectedItemIds.length) },
                        )
                      : getLocalizedText(
                          "rentmatch.applications.drag_single_candidate",
                        )}
                  </S.DragPreviewPill>
                </div>
              </div>,
              document.body,
            );
          }

          return (
            <S.TableRow
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              isDragging={snapshot.isDragging}
              onClick={() => {
                setQueryParamValue({ applicationId: item.id });
              }}
              onMouseDown={(e: any) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setClickX(e.clientX - rect.left);
              }}
            >
              {renderRowContent()}
            </S.TableRow>
          );
        }}
      </Draggable>

      {showDuplicateModal && (
        <MultiStepForm
          formId={`duplicate-application-${formId}`}
          schemas={createDuplicateApplicationForm({ broker, propertyId })}
          asModal={true}
          withAside={false}
          onSuccess={handleDuplicate as any}
          modalProps={{ onClose: handleCloseModal, width: "small" }}
        />
      )}
      {showOtherAppsModal && (
        <OtherApplicationsModal
          candidateName={getName(contacts[0] || {})}
          properties={item.otherApplicationsMeta?.properties ?? []}
          onClose={() => setShowOtherAppsModal(false)}
        />
      )}
    </>
  );
});
